import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { adminAPI } from '../config/api';
import AdminSidebar from '../components/AdminSidebar';
import { getCategoryName } from '../utils/adminFields';
import {
  formatMoney,
  formatRentalDate,
  getBookedFor,
  getBookedFrom,
  getDropoffTime,
  getRentalDays,
  getRentalLineTotal,
} from '../utils/rentalFields';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const DASHBOARD_CHART_COLORS = ['#C6A85E', '#080151', '#D8BF7A', '#7E6A3A', '#EFE7D2', '#4A4663'];

function getBookingStatus(item) {
  return (item?.bookingStatus || item?.booking_status || item?.status || 'Pending Review').toString();
}

function Admin({ categories, products }) {
  const [users, setUsers] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [wishlists, setWishlists] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let active = true;

    async function fetchAdminData() {
      const [usersResult, cartsResult, wishlistsResult, msgsResult] = await Promise.allSettled([
        adminAPI.getUsers(),
        adminAPI.getAllCarts(),
        adminAPI.getAllWishlists(),
        adminAPI.getMessages(),
      ]);

      if (!active) return;

      if (usersResult.status === 'fulfilled') setUsers(usersResult.value.data || []);
      if (cartsResult.status === 'fulfilled') {
        setCartItems(cartsResult.value.data?.cartItems || []);
        setCartTotal(cartsResult.value.data?.totalCost || 0);
      }
      if (wishlistsResult.status === 'fulfilled') setWishlists(wishlistsResult.value.data || []);
      if (msgsResult.status === 'fulfilled') setContactMsgs(msgsResult.value.data || []);
    }

    fetchAdminData();
    return () => {
      active = false;
    };
  }, []);

  const categoryNameById = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((category) => {
      map.set(String(category.id), getCategoryName(category));
    });
    return map;
  }, [categories]);

  const vehicleRows = useMemo(() => {
    const term = filter.toLowerCase();
    return (products || [])
      .map((product) => ({
        id: product.id,
        vehicleName: product.name,
        bookingStatus: product.bookingStatus || 'Available',
        price: Number(product.price || 0),
        categoryId: product.categoryId,
        category: product.categoryName || categoryNameById.get(String(product.categoryId)) || 'Uncategorized',
      }))
      .filter((row) => (
        !term ||
        row.vehicleName?.toLowerCase().includes(term) ||
        row.bookingStatus?.toLowerCase().includes(term) ||
        row.category?.toLowerCase().includes(term)
      ));
  }, [products, categoryNameById, filter]);

  const bookingRows = useMemo(() => (
    cartItems.map((item) => {
      const user = item.user || users.find((candidate) => candidate.id === item.userId);
      const days = getRentalDays(item);
      return {
        id: item.id,
        vehicle: item.product?.name || 'Vehicle',
        user: user ? `${user.firstName} ${user.lastName}` : `User ${item.userId || ''}`,
        bookedFrom: getBookedFrom(item),
        bookedFor: getBookedFor(item),
        dropoffTime: getDropoffTime(item),
        days,
        price: getRentalLineTotal(item),
      };
    })
  ), [cartItems, users]);

  const chartData = useMemo(() => {
    const labels = (categories || []).map((category) => getCategoryName(category));
    const data = (categories || []).map((category) => (
      (products || []).filter((product) => String(product.categoryId) === String(category.id)).length
    ));
    return {
      labels,
      datasets: [{
        data,
        backgroundColor: DASHBOARD_CHART_COLORS,
        borderColor: 'rgba(255,255,255,0.58)',
        borderWidth: 2,
      }],
    };
  }, [categories, products]);

  const revenueChartData = useMemo(() => {
    const labels = (categories || []).map((category) => getCategoryName(category));
    const data = (categories || []).map((category) => {
      const categoryProducts = (products || []).filter(
        (product) => String(product.categoryId) === String(category.id)
      );
      const productIds = new Set(categoryProducts.map((p) => String(p.id)));
      return cartItems
        .filter((item) => productIds.has(String(item.product?.id)))
        .reduce((sum, item) => sum + getRentalLineTotal(item), 0);
    });
    return {
      labels,
      datasets: [{
        label: 'Revenue',
        data,
        backgroundColor: '#C6A85E',
        borderRadius: 10,
      }],
    };
  }, [categories, products, cartItems]);

  const bookedCount = (products || []).filter((product) => (
    ['Booking Confirmed', 'Checked Out'].includes(product.bookingStatus)
  )).length;
  const availableCount = (products || []).filter((product) => (
    (product.bookingStatus || 'Available').toLowerCase() === 'available'
  )).length;
  const bookedPercent = products?.length ? Math.round((bookedCount / products.length) * 100) : 0;
  const fleetReadinessPercent = products?.length ? Math.round((availableCount / products.length) * 100) : 0;
  const pendingBookingCount = cartItems.filter((item) => (
    getBookingStatus(item).toLowerCase().includes('pending')
  )).length || cartItems.length;
  const messageCount = contactMsgs.length;
  const totalRentalDays = bookingRows.reduce((sum, row) => sum + row.days, 0);
  const privilegedUserCount = users.filter((user) => (user.role || 'user') !== 'user').length;
  const activeAccessScopeCount = new Set(
    users.flatMap((user) => (Array.isArray(user.permissions) ? user.permissions : []))
  ).size;

  const workflowItems = [
    {
      label: 'Bookings',
      detail: `${pendingBookingCount} awaiting review`,
      value: cartItems.length,
      percent: Math.min(100, cartItems.length * 24),
      icon: 'bi-calendar2-check',
      to: '/admin/bookings',
    },
    {
      label: 'Fleet',
      detail: `${availableCount} vehicles available`,
      value: products?.length || 0,
      percent: fleetReadinessPercent,
      icon: 'bi-car-front',
      to: '/admin/vehicles',
    },
    {
      label: 'Clients',
      detail: `${users.length} verified accounts`,
      value: users.length,
      percent: Math.min(100, users.length * 12),
      icon: 'bi-people',
      to: '/admin/users',
    },
    {
      label: 'Access',
      detail: `${privilegedUserCount} privileged accounts`,
      value: privilegedUserCount,
      percent: Math.min(100, activeAccessScopeCount * 14),
      icon: 'bi-shield-lock',
      to: '/admin/access',
    },
    {
      label: 'Support',
      detail: `${messageCount} messages received`,
      value: messageCount,
      percent: Math.min(100, messageCount * 20),
      icon: 'bi-chat-left-text',
      to: '/admin/messages',
    },
  ];

  const goalSteps = [
    { label: 'Booking Review', to: '/admin/bookings', active: true },
    { label: 'Fleet Readiness', to: '/admin/vehicles' },
    { label: 'Client Accounts', to: '/admin/users' },
    { label: 'Access Control', to: '/admin/access' },
    { label: 'Support Desk', to: '/admin/messages' },
  ];

  const stats = [
    { title: 'Booking Queue', value: cartItems.length, helper: `${pendingBookingCount} pending review`, icon: 'bi-calendar-check' },
    { title: 'Active Value', value: formatMoney(cartTotal), helper: `${totalRentalDays} rental days planned`, icon: 'bi-currency-dollar' },
    { title: 'Fleet Ready', value: `${fleetReadinessPercent}%`, helper: `${availableCount} of ${products?.length || 0} available`, icon: 'bi-car-front' },
    { title: 'Access Scopes', value: activeAccessScopeCount, helper: `${privilegedUserCount} privileged account${privilegedUserCount === 1 ? '' : 's'}`, icon: 'bi-shield-lock' },
    { title: 'Saved Interest', value: wishlists.length, helper: 'Wishlist demand signals', icon: 'bi-heart' },
  ];

  const quickActions = [
    { label: 'Review bookings', text: 'Approve, update, or remove booking requests.', icon: 'bi-calendar-check', to: '/admin/bookings' },
    { label: 'Add vehicle', text: 'Publish a new fleet listing with pricing and images.', icon: 'bi-plus-circle', to: '/admin/vehicle/new' },
    { label: 'Manage categories', text: 'Keep vehicle groups and fleet structure clean.', icon: 'bi-tags', to: '/admin/category' },
    { label: 'Control access', text: 'Assign roles and permission scopes across accounts.', icon: 'bi-shield-lock', to: '/admin/access' },
  ];
  const recentMessages = contactMsgs.slice(0, 4);

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content admin-home-content">
        <div className="dashboard-page-inner">
          <section className="dashboard-goal-hero admin-goal-hero">
            <h1 className="section-title text-primary-theme">Hi, DeRoyalty Admin!</h1>
            <p className="text-secondary-theme">What&apos;s your operations goal today?</p>
            <nav className="dashboard-goal-steps" aria-label="Admin dashboard workflow">
              {goalSteps.map((step, index) => (
                <React.Fragment key={step.label}>
                  <Link
                    to={step.to}
                    className={`dashboard-goal-step ${step.active ? 'active' : ''}`}
                  >
                    {step.label}
                  </Link>
                  {index < goalSteps.length - 1 && (
                    <span className="dashboard-goal-connector" aria-hidden="true" />
                  )}
                </React.Fragment>
              ))}
            </nav>
          </section>

          <section className="dashboard-focus-grid admin-focus-grid">
            <article className="dashboard-progress-card">
              <div className="dashboard-progress-heading">Progress</div>
              <div className="user-dashboard-progress-list">
                {workflowItems.map((item) => (
                  <Link to={item.to} key={item.label} className="user-dashboard-progress-row admin-progress-link">
                    <i className={`bi ${item.icon}`} />
                    <div>
                      <span>{item.label}</span>
                      <div><span style={{ width: `${item.percent}%` }} /></div>
                    </div>
                    <strong>{item.value}</strong>
                  </Link>
                ))}
              </div>
            </article>

            <aside className="dashboard-focus-panel admin-dashboard-action-panel">
              <div className="dashboard-focus-copy">
                <span className="dashboard-mini-label">Priority workflow</span>
                <h2>Review booking requests before fleet allocation</h2>
                <p>
                  Keep dates, vehicle assignments, customer accounts, and rental totals synchronized before a vehicle is released.
                </p>
                <Link to="/admin/bookings" className="dashboard-action-strip">
                  Open booking manager
                  <i className="bi bi-arrow-right" />
                </Link>
              </div>
              <div className="dashboard-feature-visual" aria-hidden="true">
                <div className="dashboard-feature-score">{fleetReadinessPercent}%</div>
                <span>Fleet ready</span>
                <i className="bi bi-car-front-fill" />
              </div>
            </aside>
          </section>

          <section className="admin-stats-grid admin-stats-grid--command" aria-label="Admin dashboard summary">
            {stats.map((stat) => (
              <article key={stat.title} className="admin-stat-card">
                <div>
                  <p>{stat.title}</p>
                  <strong>{stat.value}</strong>
                  <span>{stat.helper}</span>
                </div>
                <i className={`bi ${stat.icon}`} />
              </article>
            ))}
          </section>

          <section className="admin-command-grid admin-command-grid--quick">
            <div className="admin-quick-actions">
              <div className="admin-card-header">
                <h2 className="section-title text-primary-theme">Fast actions</h2>
                <Link to="/admin/vehicles" className="admin-action-link">View fleet</Link>
              </div>
              <div className="admin-quick-action-list">
                {quickActions.map((action) => (
                  <Link to={action.to} key={action.label} className="admin-quick-action">
                    <i className={`bi ${action.icon}`} />
                    <div>
                      <strong>{action.label}</strong>
                      <span>{action.text}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <div className="admin-progress-card admin-progress-card--compact">
              <p className="text-accent">Operational progress</p>
              <h2 className="section-title text-primary-theme">Today&apos;s control board</h2>
              <p className="text-secondary-theme">
                Scan the major workstreams and jump directly into the area that needs attention.
              </p>
            </div>
          </section>

          <section className="admin-insights-grid">
            <div className="admin-insight-card">
              <div className="admin-card-header">
                <div>
                  <p className="text-accent">Fleet overview</p>
                  <h2 className="section-title text-primary-theme">Vehicles ({products?.length || 0})</h2>
                </div>
                <input
                  type="search"
                  placeholder="Filter by name, status, category..."
                  value={filter}
                  onChange={(event) => setFilter(event.target.value)}
                  className="admin-search compact"
                />
              </div>
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Vehicle Name</th>
                      <th>Booking Status</th>
                      <th>Price</th>
                      <th>Category</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleRows.slice(0, 8).map((row) => (
                      <tr key={row.id}>
                        <td>
                          <Link to={`/vehicle/show/${row.id}/${encodeURIComponent(row.vehicleName)}`} className="text-primary-theme no-underline">
                            {row.vehicleName}
                          </Link>
                        </td>
                        <td className="text-secondary-theme">{row.bookingStatus}</td>
                        <td className="font-bold text-accent">{formatMoney(row.price)}</td>
                        <td>
                          <Link to={`/category/show/${row.categoryId}`} className="text-secondary-theme no-underline">
                            {row.category}
                          </Link>
                        </td>
                      </tr>
                    ))}
                    {!vehicleRows.length && (
                      <tr>
                        <td colSpan="4" className="py-8 text-center text-secondary-theme">No vehicles found</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="admin-insight-card">
              <div className="admin-card-header">
                <Link to="/admin/category" className="section-title text-2xl text-primary-theme no-underline">
                  Categories
                </Link>
              </div>
              <div className="admin-chart-wrap">
                {categories?.length ? (
                  <Doughnut
                    data={chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: {
                          labels: {
                            color: getComputedStyle(document.documentElement).getPropertyValue('--text-primary') || '#fff',
                          },
                        },
                      },
                    }}
                  />
                ) : (
                  <p className="text-secondary-theme">No categories available.</p>
                )}
              </div>
              <Link to="/admin/vehicles" className="btn-luxury-outline mt-4 block text-center">
                View all Vehicles
              </Link>
            </div>
          </section>

          <section className="admin-insights-grid">
            <div className="admin-insight-card">
              <div className="admin-card-header">
                <div>
                  <p className="text-accent">Revenue</p>
                  <h2 className="section-title text-primary-theme">Value by category</h2>
                </div>
              </div>
              <div className="admin-chart-wrap">
                {categories?.length ? (
                  <Bar
                    data={revenueChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                      },
                      scales: {
                        x: {
                          ticks: {
                            color: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary') || '#aaa',
                          },
                          grid: { display: false },
                        },
                        y: {
                          ticks: {
                            color: getComputedStyle(document.documentElement).getPropertyValue('--text-secondary') || '#aaa',
                          },
                          grid: { color: 'rgba(198,168,94,0.1)' },
                        },
                      },
                    }}
                  />
                ) : (
                  <p className="text-secondary-theme">No revenue data available.</p>
                )}
              </div>
            </div>

            <div className="admin-insight-card">
              <div className="admin-card-header">
                <div>
                  <p className="text-accent">Support</p>
                  <h2 className="section-title text-primary-theme">Recent messages</h2>
                </div>
                <Link to="/admin/messages" className="admin-action-link">
                  View All
                </Link>
              </div>
              <div className="admin-message-list">
                {recentMessages.length ? (
                  recentMessages.map((msg) => (
                    <div key={msg.id} className="admin-message-item">
                      <div className="admin-message-item-header">
                        <strong className="text-primary-theme">{msg.name}</strong>
                        <span className="text-secondary-theme text-sm">{msg.email}</span>
                      </div>
                      {msg.message && (
                        <p className="admin-message-item-body">{msg.message}</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="py-8 text-center text-secondary-theme">No messages yet</p>
                )}
              </div>
            </div>
          </section>

          <section className="admin-insight-card admin-bookings-overview">
            <div className="admin-card-header">
              <div>
                <p className="text-accent">Reservations</p>
                <h2 className="section-title text-primary-theme">Booking activity</h2>
              </div>
              <div className="admin-progress">
                <span>Currently booked</span>
                <div><span style={{ width: `${bookedPercent}%` }} /></div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Vehicle</th>
                    <th>User</th>
                    <th>Pickup Date</th>
                    <th>Dropoff Date</th>
                    <th>Dropoff Time</th>
                    <th>Days</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {bookingRows.slice(0, 10).map((row) => (
                    <tr key={row.id}>
                      <td>{row.vehicle}</td>
                      <td className="text-secondary-theme">{row.user}</td>
                      <td className="text-secondary-theme">{formatRentalDate(row.bookedFrom)}</td>
                      <td className="text-secondary-theme">{formatRentalDate(row.bookedFor)}</td>
                      <td className="text-secondary-theme">{row.dropoffTime || '-'}</td>
                      <td>{row.days}</td>
                      <td className="font-bold text-accent">{formatMoney(row.price)}</td>
                    </tr>
                  ))}
                  {!bookingRows.length && (
                    <tr>
                      <td colSpan="7" className="py-8 text-center text-secondary-theme">No bookings found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default Admin;
